import { useEffect, useRef } from 'react';
import type { CluePayload } from '@guess/engine';
import type { ClueProps } from './ClueRenderer';
import s from './clue.module.scss';

type ImagePayload = Extract<CluePayload, { kind: 'image' }>;

/**
 * L'image qui se depixelise.
 *
 * Ni palier de cote, ni courbe continue en "taille de bloc" (elle donnait
 * l'impression fausse d'accelerer en fin de manche, puisque le NOMBRE de
 * blocs = largeur / taille varie en 1/x). Le nombre de blocs grimpe par etape
 * de `stepSec` secondes, mais GEOMETRIQUEMENT, pas lineairement — sinon
 * c'est la nettete percue qui accelere faussement en DEBUT de manche (voir
 * `blocksAt` ci-dessous) :
 *
 *   N etapes = duree de la manche (s) / stepSec
 *   blocs(etape) = startBlocks * (endBlocks / startBlocks) ^ (etape / N)
 *
 * `startBlocks`/`endBlocks`/`stepSec` viennent du serveur (voir
 * `reveal-config.ts`), toujours : le client ne decide jamais du depart ni de
 * la cadence, seulement — via son propre chrono, deja fiable pour le
 * decompte affiche — de l'etape ou on en est.
 *
 * Technique de rendu : a chaque etape, l'image source est redessinee sur un
 * canvas hors-ecran a une resolution reduite (mosaique), puis cette mosaique
 * est reagrandie sans lissage sur le canvas visible — la methode classique
 * pour un vrai pixel art (pas juste `filter: blur`).
 */
export function ImageClue({ payload, elapsedMs, totalMs }: ClueProps<ImagePayload>) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const smallCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const blocks = blocksAt(payload, elapsedMs, totalMs);

  function draw(blockCount: number) {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { width, height } = canvas;
    const size = width / blockCount;

    if (size <= 1) {
      ctx.imageSmoothingEnabled = true;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      return;
    }

    if (!smallCanvasRef.current) smallCanvasRef.current = document.createElement('canvas');
    const small = smallCanvasRef.current;
    const smallCtx = small.getContext('2d');
    if (!smallCtx) return;

    // La mosaique : une image minuscule, dont chaque pixel deviendra un bloc.
    small.width = Math.max(1, Math.round(blockCount));
    small.height = Math.max(1, Math.round(height / size));
    smallCtx.imageSmoothingEnabled = true;
    smallCtx.drawImage(img, 0, 0, small.width, small.height);

    // Le reagrandissement, sans lissage : c'est lui qui dessine les blocs nets.
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(small, 0, 0, small.width, small.height, 0, 0, width, height);
  }

  // Charge l'image source une seule fois par URL, hors DOM.
  useEffect(() => {
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      draw(blocks);
    };
    img.src = payload.url;
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- une seule fois par URL, pas par etape
  }, [payload.url]);

  // Redessine a chaque nouvelle etape (change au plus toutes les stepSec).
  useEffect(() => {
    draw(blocks);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- draw lit les refs, pas besoin de le lister
  }, [blocks]);

  return (
    <figure className={s.imageFrame}>
      <canvas ref={canvasRef} className={s.image} aria-label="Indice visuel, plus ou moins pixelise" />
    </figure>
  );
}

/**
 * Le nombre de blocs pour l'etape courante : `Math.floor`, jamais interpole
 * entre deux etapes.
 *
 * Progression GEOMETRIQUE en nombre de blocs, pas lineaire : la taille d'un
 * bloc (donc la nettete percue) varie en 1/blocs, si bien qu'une ligne droite
 * en nombre de blocs rend l'image quasi nette des le premier tiers du temps,
 * puis ne change plus grand-chose ensuite (voir le ticket qui a motive ce
 * choix). En multipliant par un facteur constant a chaque etape au lieu
 * d'ajouter un increment constant, l'image reste grossiere plus longtemps et
 * la plus grande partie du gain de nettete arrive sur la fin.
 */
function blocksAt(payload: ImagePayload, elapsedMs: number, totalMs: number): number {
  const { startBlocks, endBlocks, stepSec } = payload;
  if (totalMs <= 0 || stepSec <= 0) return startBlocks;
  const start = Math.max(1, startBlocks);

  const stepCount = Math.max(1, Math.floor(totalMs / 1000 / stepSec));
  const step = Math.min(stepCount, Math.floor(elapsedMs / 1000 / stepSec));
  const ratio = step / stepCount;
  return start * Math.pow(endBlocks / start, ratio);
}
